import JwtDecode from "jwt-decode";
import { User as AuthUser, UserManager } from "oidc-client-ts";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useIdle } from "react-use";
import { ensure } from "sprint-common-web";

import { AuthConfigContext } from "../configuration/auth/AuthConfigurationContext";
import { AppConfigContext } from "../configuration/Configuration";
import { AuthPage } from "./AuthPage";
import { SprintAccessRight, assetsAccessRightsMatrix } from "./SprintAccessRight";
import { parseRoles, User, UserRole } from "./User";

export interface IAuthContext {
    sprintUser: User;
    logout(): void;
    isAdmin: boolean;
}

export const AuthContext = React.createContext<IAuthContext>({} as IAuthContext);

export const AuthConsumer = AuthContext.Consumer;

type AuthProviderProps = React.PropsWithChildren<unknown>;

export const AuthProvider: React.FunctionComponent<AuthProviderProps> = ({ children }: AuthProviderProps) => {
    const authConfiguration = useContext(AuthConfigContext);

    const isIdle = useIdle(authConfiguration.noActivityLogoutTimeInMinutes * 60 * 1000);
    const history = useHistory();

    const [authUser, setAuthUser] = useState<AuthUser | null | undefined>();
    const [user, setUser] = useState<User | undefined>(
        !authConfiguration.authenticationEnabled ? authConfiguration.defaultUser : undefined
    );

    const userManager = useRef<UserManager>();

    const isAdmin = user?.roles.includes(UserRole.Admin) ?? false;
    const isLoggedIn = !authConfiguration.authenticationEnabled || !!authUser;

    useEffect(() => {
        if (!userManager.current && authConfiguration) {
            initializeUserManager();

            if (authConfiguration.authenticationEnabled) {
                setUsers();
            }
        }
    }, [authConfiguration]);

    useEffect(() => {
        if (isIdle) {
            logout();
        }
    }, [isIdle]);

    useEffect(() => {
        if (authUser) {
            const newUser = createSprintUser(authUser);

            if (!newUser.roles.length) {
                redirectToAuthUserErrorPage();
            }
            setUser(newUser);
        } else {
            setUser(undefined);
        }
    }, [authUser]);

    const initializeUserManager = () => {
        const baseUrl = `${window.location.origin}${window.location.pathname}`;

        userManager.current = new UserManager({
            authority: authConfiguration.authority,
            client_id: authConfiguration.clientId,
            redirect_uri: `${baseUrl}${authConfiguration.redirectUri}`,
            automaticSilentRenew: false
        });

        userManager.current?.events.addAccessTokenExpiring(() => {
            silentRenewToken();
        });

        userManager.current?.events.addAccessTokenExpired(() => {
            logout();
        });
    };

    const login = () => {
        userManager.current?.signinRedirect({ state: window.location.href });
    };

    const logout = () => {
        userManager.current?.removeUser();
        window.location.href = authConfiguration.logoutUri;
    };

    const callback = () => {
        userManager.current
            ?.signinRedirectCallback()
            .then((response) => {
                window.location.href = String(response.state);
            })
            .catch(() => {
                redirectToAuthUserErrorPage();
            });
    };

    const silentRenewToken = () => {
        userManager.current?.signinSilent().then((response) => {
            if (response) {
                setAuthUser(response);
            }
        });
    };

    const redirectToAuthUserErrorPage = () => {
        history.push("/auth-user-error");
    };

    const setUsers = () => {
        userManager.current?.getUser().then((userResponse) => {
            setAuthUser(userResponse);
        });
    };

    if (!user) {
        return (
            <AuthPage
                isInitializedUserManager={!!userManager.current}
                isLoggedIn={isLoggedIn}
                callback={callback}
                login={login}
            />
        );
    }

    return <AuthContext.Provider value={{ logout, sprintUser: user, isAdmin }}>{children}</AuthContext.Provider>;
};

const createSprintUser = (newAuthUser: AuthUser): User => {
    ensure(newAuthUser.id_token);

    const decodedToken = getDecodedToken(newAuthUser.id_token);
    const roles = decodedToken["custom:role"];
    const originators = decodedToken["custom:originator_ids"];

    const userRoles = parseRoles(roles);
    const accessRights = getAccessRights(userRoles);

    const username = decodedToken["cognito:username"];
    const userEmail = decodedToken["email"];

    return {
        name: username,
        email: userEmail,
        roles: userRoles,
        accessRights,
        accessToken: newAuthUser.access_token,
        idToken: newAuthUser.id_token,
        originatorIds: originators ? originators.replace("[", "").replace("]", "").split(",") : []
    };
};

const getDecodedToken = (tokenToDecode: string): Record<string, string> => {
    try {
        return JwtDecode(tokenToDecode);
    } catch (error) {
        throw new Error("Token is missing.");
    }
};

const getAccessRights = (roles: UserRole[]) => {
    const filteredRoles = roles.filter((group: UserRole) => !!assetsAccessRightsMatrix[group]);

    if (!filteredRoles.length) {
        return [];
    }

    return filteredRoles
        .map((group: UserRole): SprintAccessRight[] => assetsAccessRightsMatrix[group])
        .reduce((previous: SprintAccessRight[], next: SprintAccessRight[]) => previous.concat(next));
};
